// src/validation/setupValidation.ts
import { configure, defineRule } from 'vee-validate';
import * as rules from '@vee-validate/rules';

// eslint-disable-next-line no-unused-vars
type TFn = (key: string, params?: Record<string, any>) => string;
type GetTFn = () => TFn;

const pickRule = (key: string) => (rules as any)[key];

const asBooleanRule = (ruleFn: any) => {
  return (value: any, params: any, ctx: any) => {
    const res = ruleFn?.(value, params, ctx);
    if (typeof res === 'string') return false;
    return res;
  };
};

const pickParam = (p: any, key: string) => {
  if (p == null) return undefined;
  if (typeof p === 'number' || typeof p === 'string') return p;
  if (Array.isArray(p)) return p[0];
  if (typeof p === 'object') return p[key] ?? p[0];
  return undefined;
};

const isEmpty = (v: any) => v === null || v === undefined || v === '';

export default function setupValidation(getT: GetTFn) {
  // ---------------- base rules ----------------
  defineRule('required', asBooleanRule(pickRule('required')));
  defineRule('confirmed', asBooleanRule(pickRule('confirmed')));
  defineRule('numeric', (value: any, params: any, ctx: any) => {
    if (isEmpty(value)) return true;
    return asBooleanRule(pickRule('numeric'))(value, params, ctx);
  });
  defineRule('max_value', (value: any, params: any, ctx: any) => {
    if (isEmpty(value)) return true;
    return asBooleanRule(pickRule('max_value'))(value, params, ctx);
  });
  defineRule('alpha_num', asBooleanRule(pickRule('alpha_num')));

  // ---------------- custom rules ----------------
  defineRule('max', (value: any, limit: any) => {
    if (isEmpty(value)) return true;
    const length = Number(pickParam(limit, 'length') ?? limit);
    return String(value ?? '').length <= length;
  });

  defineRule('min', (value: any, limit: any) => {
    if (isEmpty(value)) return true;
    const length = Number(pickParam(limit, 'length') ?? limit);
    return String(value ?? '').length >= length;
  });

  defineRule('min_value', (value: any, minParam: any) => {
    if (isEmpty(value)) return true;
    const min = Number(pickParam(minParam, 'min') ?? minParam);
    const num = Number(value);
    if (!Number.isFinite(num)) return false;
    return num >= min;
  });

  defineRule('decimal', (value: any, decParam: any) => {
    if (isEmpty(value)) return true;

    const decimalsRaw = pickParam(decParam, 'decimals') ?? decParam;
    const decimals = Number(decimalsRaw);

    const str = String(value);
    const d = Number.isFinite(decimals) ? Math.max(0, decimals) : 0;

    const regex = d === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${d}})?$`);
    return regex.test(str);
  });

  // ---------------- messages/config ----------------
  configure({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false,

    generateMessage: (ctx: any) => {
      const t = getT();
      const rule = String(ctx?.rule?.name ?? '');
      const params = ctx?.rule?.params;

      switch (rule) {
        case 'alpha_num':
          return t('validation.alphaNum');
        case 'required':
          return t('validation.required');
        case 'numeric':
          return t('validation.numeric');
        case 'confirmed':
          return t('validation.confirmed');

        case 'max': {
          const length = pickParam(params, 'length') ?? params;
          return t('validation.maxCharacters', { length });
        }

        case 'min': {
          const length = pickParam(params, 'length') ?? params;
          return t('validation.minCharacters', { length });
        }

        case 'min_value': {
          const min = pickParam(params, 'min') ?? params;
          return t('validation.minValue', { min });
        }

        case 'max_value':
          return t('validation.maxValue');

        case 'decimal': {
          const decimals = pickParam(params, 'decimals') ?? params;
          return t('validation.decimal', { decimals });
        }

        default:
          return t('validation.required');
      }
    }
  });
}
